import { useState, useEffect } from 'react'
import { useAuth } from '@/lib/contexts/AuthContext'
import { getIncomingInvites, acceptAccountInvite, declineAccountInvite } from '@/lib/api/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'

import { toast } from 'sonner'
import { HugeiconsIcon } from "@hugeicons/react"
import {
  AlertCircleIcon,
  Cancel01Icon,
  CheckmarkCircle01Icon,
  Clock01Icon,
  Notification01Icon,
  UserGroupIcon,
} from "@hugeicons/core-free-icons"

interface IncomingInvite {
  id: string
  token: string
  inviterName: string
  inviterEmail: string
  accountName: string
  propertyCount: number
  properties: string[]
  invitedAt: string
  expiresAt: string
}

interface SharedAccessNotificationProps {
  onInviteHandled?: () => void
  className?: string
}

export default function SharedAccessNotification({ 
  onInviteHandled,
  className 
}: SharedAccessNotificationProps) {
  const { token } = useAuth()
  const [invites, setInvites] = useState<IncomingInvite[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [processingInvites, setProcessingInvites] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (token) {
      loadIncomingInvites()
    }
  }, [token])

  const loadIncomingInvites = async () => {
    if (!token) return

    try {
      setIsLoading(true)
      const response = await getIncomingInvites(token)
      setInvites(Array.isArray(response) ? response : response?.invites || [])
    } catch (error) {
      console.error('Failed to load incoming invites:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleAcceptInvite = async (invite: IncomingInvite) => {
    if (processingInvites.has(invite.id)) return

    try {
      setProcessingInvites(prev => new Set([...prev, invite.id]))
      
      await acceptAccountInvite(invite.token, token!)
      
      toast.success(`Joined ${invite.accountName}`)
      
      // Remove the invite from the list
      setInvites(prev => prev.filter(inv => inv.id !== invite.id))
      
      onInviteHandled?.()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to accept invitation'
      toast.error(message)
    } finally {
      setProcessingInvites(prev => {
        const newSet = new Set(prev)
        newSet.delete(invite.id)
        return newSet
      })
    }
  }

  const handleDeclineInvite = async (invite: IncomingInvite) => {
    if (processingInvites.has(invite.id)) return

    try {
      setProcessingInvites(prev => new Set([...prev, invite.id]))
      
      await declineAccountInvite(invite.token, token!)
      
      toast.success('Invitation declined')
      
      // Remove the invite from the list
      setInvites(prev => prev.filter(inv => inv.id !== invite.id))
      
      onInviteHandled?.()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to decline invitation'
      toast.error(message)
    } finally {
      setProcessingInvites(prev => {
        const newSet = new Set(prev)
        newSet.delete(invite.id)
        return newSet
      })
    }
  }

  const isInviteExpired = (expiresAt: string) => {
    return new Date() > new Date(expiresAt)
  }

  const getTimeRemaining = (expiresAt: string) => {
    const now = new Date()
    const expires = new Date(expiresAt)
    const diff = expires.getTime() - now.getTime()
    
    if (diff <= 0) return 'Expired'
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} left`
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} left`
    return 'Less than 1 hour left'
  }

  if (isLoading) {
    return null
  }

  if (invites.length === 0) {
    return null
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
        <HugeiconsIcon icon={Notification01Icon} size={16} />
        Team Invitations ({invites.length})
      </div>
      
      {invites.map((invite) => {
        const isExpired = isInviteExpired(invite.expiresAt)
        const isProcessing = processingInvites.has(invite.id)
        
        return (
          <Card key={invite.id} className="relative">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <HugeiconsIcon icon={UserGroupIcon} size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-sm">Team invitation</CardTitle>
                    <CardDescription className="text-xs">
                      From {invite.inviterName} ({invite.inviterEmail})
                    </CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isExpired ? (
                    <Badge variant="destructive">Expired</Badge>
                  ) : (
                    <Badge variant="secondary">
                      <HugeiconsIcon icon={Clock01Icon} size={12} className="mr-1" />
                      {getTimeRemaining(invite.expiresAt)}
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-3">
              <Alert>
                <HugeiconsIcon icon={AlertCircleIcon} size={16} />
                <AlertDescription>
                  Join <strong>{invite.accountName}</strong> and get access to {invite.propertyCount} properties
                </AlertDescription>
              </Alert>

              {invite.properties.length > 0 && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-2">Properties included:</p>
                  <div className="space-y-1">
                    {invite.properties.slice(0, 3).map((property, index) => (
                      <div key={index} className="flex items-center text-sm text-gray-600">
                        <div className="w-2 h-2 bg-success rounded-full mr-2" />
                        {property}
                      </div>
                    ))}
                    {invite.properties.length > 3 && (
                      <div className="text-sm text-gray-500 ml-4">
                        + {invite.properties.length - 3} more properties
                      </div>
                    )}
                  </div>
                </div>
              )}

              {isExpired ? (
                <Alert variant="destructive">
                  <HugeiconsIcon icon={AlertCircleIcon} size={16} />
                  <AlertDescription>
                    This invitation has expired. Contact {invite.inviterName} for a new invitation.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={() => handleAcceptInvite(invite)}
                    disabled={isProcessing}
                    size="sm"
                    className="flex-1"
                  >
                    {isProcessing ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <HugeiconsIcon icon={CheckmarkCircle01Icon} size={12} className="mr-2" />
                        Accept
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => handleDeclineInvite(invite)}
                    disabled={isProcessing}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    <HugeiconsIcon icon={Cancel01Icon} size={12} className="mr-2" />
                    Decline
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
